import { useEffect, useRef, useState } from "react";
import { captureGps } from "../lib/gps";
import type { GpsResult, GpsError } from "../lib/gps";

interface Props {
  onCapture: (result: GpsResult) => void;
  captured: GpsResult | null;
}

const MAX_ACCURACY_M = 100;
const DETECTION_RADIUS_M = 150;

export function GpsCapture({ onCapture, captured }: Props) {
  const [liveAccuracy, setLiveAccuracy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const watchIdRef = useRef<number | null>(null);
  const lastAccuracyUpdateRef = useRef<number>(0);

  // Live accuracy watch
  useEffect(() => {
    if (!navigator.geolocation) return;

    const startWatch = () => {
      if (watchIdRef.current !== null) return;
      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          const now = Date.now();
          if (now - lastAccuracyUpdateRef.current < 1000) return;
          lastAccuracyUpdateRef.current = now;
          setLiveAccuracy(pos.coords.accuracy);
        },
        () => setLiveAccuracy(null),
        { enableHighAccuracy: true, maximumAge: 1000, timeout: 10000 },
      );
    };

    const stopWatch = () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stopWatch();
      } else {
        startWatch();
      }
    };

    onVisibilityChange();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      stopWatch();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  async function handleCapture() {
    if (!navigator.onLine) {
      setError("No network — check connectivity");
      return;
    }
    setCapturing(true);
    setError(null);

    try {
      const result = await captureGps(MAX_ACCURACY_M, DETECTION_RADIUS_M);
      onCapture(result);
    } catch (e) {
      const err = e as GpsError;
      switch (err.code) {
        case "PERMISSION_DENIED":
          setError("Location permission denied. Please allow access in browser settings.");
          break;
        case "POSITION_UNAVAILABLE":
          setError("Position unavailable. Move to an area with better signal.");
          break;
        case "TIMEOUT":
          setError("Location timed out. Try again in an open area.");
          break;
        case "ACCURACY_TOO_LOW":
          setError(
            `Accuracy too low (±${Math.round(err.accuracyM)} m). Need ≤${MAX_ACCURACY_M} m — wait for better signal.`,
          );
          break;
        default:
          setError("Unknown location error. Try again.");
      }
    } finally {
      setCapturing(false);
    }
  }

  const accuracyOk = liveAccuracy !== null && liveAccuracy <= MAX_ACCURACY_M;
  const captureDisabled = capturing || !accuracyOk;

  return (
    <div style={s.container}>
      <h2 style={s.heading}>Step 1 — GPS Location</h2>
      <p style={s.body}>
        Stand at the POI. Wait for accuracy ≤{MAX_ACCURACY_M} m, then capture.
      </p>

      {!navigator.onLine && (
        <p style={s.warning}>No network — check connectivity</p>
      )}

      <div style={s.accuracyBox}>
        <span style={s.accuracyLabel}>Live accuracy</span>
        <span
          style={{
            ...s.accuracyValue,
            color: accuracyOk ? "#2d7a2d" : liveAccuracy !== null ? "#c0392b" : "#6b6b6b",
          }}
        >
          {liveAccuracy !== null ? `±${Math.round(liveAccuracy)} m` : "Waiting…"}
        </span>
      </div>

      {captured && (
        <div style={s.resultBox}>
          <span style={s.resultLabel}>Captured</span>
          <span style={s.resultValue}>
            {captured.lat.toFixed(6)}, {captured.lng.toFixed(6)}
          </span>
          <span style={s.resultMeta}>±{Math.round(captured.accuracyM)} m accuracy</span>
          <span style={s.resultMeta}>Detection radius: {captured.detectionRadiusM} m</span>
        </div>
      )}

      {error && <p style={s.error}>{error}</p>}

      <div style={s.buttonRow}>
        <button
          style={{
            ...s.btn,
            ...s.btnPrimary,
            ...(captureDisabled ? s.btnDisabled : {}),
          }}
          onClick={handleCapture}
          disabled={captureDisabled}
          aria-label="Capture GPS location"
        >
          {capturing ? "Locating…" : captured ? "Capture" : "Capture"}
        </button>

        {captured && (
          <button
            style={{ ...s.btn, ...s.btnSecondary }}
            onClick={handleCapture}
            disabled={capturing}
            aria-label="Re-acquire GPS location"
          >
            Refine
          </button>
        )}
      </div>

      {captureDisabled && !capturing && liveAccuracy !== null && liveAccuracy > MAX_ACCURACY_M && (
        <p style={s.hint}>
          Accuracy is {Math.round(liveAccuracy)} m — move outdoors or wait a moment.
        </p>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  heading: {
    fontSize: 17,
    fontWeight: 700,
    color: "#111",
    margin: 0,
  },
  body: {
    fontSize: 14,
    color: "#444",
    lineHeight: 1.5,
    margin: 0,
  },
  warning: {
    fontSize: 13,
    color: "#7a4d00",
    background: "#fff8e1",
    padding: "8px 12px",
    borderRadius: 8,
    margin: 0,
  },
  accuracyBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#f5f5f5",
    padding: "12px 16px",
    borderRadius: 10,
  },
  accuracyLabel: {
    fontSize: 13,
    color: "#6b6b6b",
    fontWeight: 500,
  },
  accuracyValue: {
    fontSize: 20,
    fontWeight: 700,
    fontFamily: "monospace",
  },
  resultBox: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    background: "#f0faf0",
    padding: "10px 14px",
    borderRadius: 10,
    borderLeft: "4px solid #2d7a2d",
  },
  resultLabel: {
    fontSize: 11,
    color: "#2d7a2d",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  resultValue: {
    fontSize: 14,
    fontFamily: "monospace",
    color: "#111",
  },
  resultMeta: {
    fontSize: 12,
    color: "#6b6b6b",
  },
  error: {
    fontSize: 13,
    color: "#c0392b",
    background: "#fdf0ef",
    padding: "10px 14px",
    borderRadius: 8,
    margin: 0,
  },
  hint: {
    fontSize: 12,
    color: "#6b6b6b",
    margin: 0,
  },
  buttonRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  btn: {
    minHeight: 48,
    padding: "0 24px",
    borderRadius: 8,
    border: "none",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnPrimary: {
    background: "#c8102e",
    color: "#fff",
  },
  btnSecondary: {
    background: "#f5f5f5",
    color: "#333",
    border: "1px solid #ddd",
  },
  btnDisabled: {
    background: "#e0e0e0",
    color: "#999",
    cursor: "not-allowed",
  },
};
